/** @typedef {import('../index.js').BaseDocData} BaseDocData */
/* eslint-disable jsdoc/no-undefined-types */
import * as Yup from 'yup';
import {
  InventoryCommitmentStatus,
  InventoryCommitmentTypes,
} from '../../constants/index.js';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

const commitmentTypeValues = Object.values(InventoryCommitmentTypes);
const commitmentStatusValues = Object.values(InventoryCommitmentStatus);

export const inventoryCommitmentSchema = Yup.object().shape({
  productDocId: Yup.string().required('productDocId is required'),
  productVersionId: Yup.string().required('productVersionId is required'),
  sku: Yup.string().required('sku is required'),
  lineItemId: Yup.string().required('lineItemId is required'),
  orderId: Yup.string().required('orderId is required'),
  type: Yup.string().oneOf(commitmentTypeValues).required('type is required'),
  originalQuantity: Yup.number().integer().min(0).required('originalQuantity is required'),
  fulfilledQuantity: Yup.number().integer().min(0).default(0)
    .required('fulfilledQuantity is required'),
  remainingQuantity: Yup.number().integer().min(0).default(0)
    .required('remainingQuantity is required'),
  status: Yup.string().oneOf(commitmentStatusValues).default(InventoryCommitmentStatus.OPEN)
    .required('status is required'),
  createdAt: YupFirestoreTimestamp.nullable(),
  updatedAt: YupFirestoreTimestamp.nullable(),
});

/**
 * @typedef {Yup.InferType<typeof inventoryCommitmentSchema>} InventoryCommitmentSchema
 * @typedef {BaseDocData & InventoryCommitmentSchema} InventoryCommitmentDocData
 */

export function getInventoryCommitmentId(type, lineItemId) {
  return `${type}_${lineItemId}`;
}

export default class InventoryCommitment {
  /** @type {InventoryCommitmentDocData['validation']} */ validation = 'passed';

  constructor({
    id,
    productDocId,
    productVersionId,
    sku,
    lineItemId,
    orderId,
    type,
    originalQuantity,
    fulfilledQuantity = 0,
    remainingQuantity = originalQuantity - fulfilledQuantity,
    status = InventoryCommitmentStatus.OPEN,
    createdAt,
    updatedAt,
  }) {
    const validatedData = inventoryCommitmentSchema.validateSync({
      productDocId,
      productVersionId,
      sku,
      lineItemId,
      orderId,
      type,
      originalQuantity,
      fulfilledQuantity,
      remainingQuantity,
      status,
      createdAt,
      updatedAt,
    });

    this.id = id || null;
    Object.assign(this, validatedData);
  }

  toFirestore({ serverTimestamp }) {
    return {
      productDocId: this.productDocId,
      productVersionId: this.productVersionId,
      sku: this.sku,
      lineItemId: this.lineItemId,
      orderId: this.orderId,
      type: this.type,
      originalQuantity: this.originalQuantity,
      fulfilledQuantity: this.fulfilledQuantity,
      remainingQuantity: this.remainingQuantity,
      status: this.status,
      createdAt: this.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  }

  static fromFirestore(docData) {
    return new InventoryCommitment(docData);
  }
}
